import { useEffect, useRef, MutableRefObject, Dispatch, SetStateAction } from 'react';
import { WebSocketAdapter } from '../utils/WebSocketAdapter';
import RoomSessionStorage from '../utils/sessionStorage';
import { getRoomCodeFromUrl, getHostCodeFromUrl, formatRoomCode, clearQueryParamFromUrl } from '../utils/roomCode';
import { clearEntryRouteFromUrl, isHostEntryPath, isJoinEntryPath, navigateToJoin, navigateToRoot } from '../utils/entryRoutes';
import { t, translateBackendError, JOIN_ROOM_NOT_FOUND_MESSAGE } from '../i18n/helpers';
import { dispatchJoinRoomError } from '../utils/joinRoomEvents';
import {
  playRoomCreated,
  playRoomJoined,
  playPlayerJoined,
  playGameStart,
  playGameFinished,
  playReturnToWaiting,
} from '../utils/sounds';
import { syncPlayersFromLeaderboard } from '../utils/leaderboardUtils';

interface RoomState {
  roomName: string;
  gameType: string;
  hostId: string;
  players: Array<{
    id: string;
    name: string;
    score: number;
    avatar?: string;
    teamId?: 'A' | 'B';
  }>;
  gameState: 'waiting' | 'playing' | 'finished';
  currentQuestion?: any;
  questionStartTime?: number;
  votes?: Record<string, number>;
  categoryVotes?: Record<string, number>;
  settings?: any;
  hostDisconnectedAt?: number | null; // When host disconnected (null when connected)
  isPremium?: boolean;
  expiresAt?: number; // Premium room expiration timestamp (milliseconds)
  isBonusLevel?: boolean;
  bonusLevelPayload?: any;
  bonusStartTime?: number;
  isPaused?: boolean;
  pausedRemainingMs?: number;
  /** Set when game-ended is received; used so end screen shows leaderboard/stats without refocus */
  finalLeaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
  gameStatistics?: any;
  teams?: {
    A: { id: 'A'; name: string; score: number };
    B: { id: 'B'; name: string; score: number };
  };
}

interface UseSocketEventsOptions {
  socket: WebSocketAdapter | null;
  roomState: RoomState | null;
  setRoomState: Dispatch<SetStateAction<RoomState | null>>;
  setConnected: Dispatch<SetStateAction<boolean>>;
  setMyPlayerId: Dispatch<SetStateAction<string | null>>;
  setIsHost: Dispatch<SetStateAction<boolean>>;
  setShowJoinRoom: Dispatch<SetStateAction<boolean>>;
  showJoinRoom: boolean;
  setReconnecting: (value: boolean) => void;
  setReconnectAttempts: (value: number) => void;
  roomStateRef: MutableRefObject<RoomState | null>;
  isLeavingRef: MutableRefObject<boolean>;
  reconnectTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  pendingStartGameRef: MutableRefObject<{ gameType: string } | null>;
  isHost: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
  startReconnection: () => void;
  clearReconnection: () => void;
  connectionErrorAttemptsRef: MutableRefObject<number>;
  setConnectionError: Dispatch<SetStateAction<string | null>>;
  failedRoomsRef?: MutableRefObject<Set<string>>;
  /** When true, game setup should show tiles immediately (restore failed or not applicable) instead of waiting max loader time */
  setRestoreAttemptSettled?: (value: boolean) => void;
  /** Called when backend says we're on wrong DO (Cloudflare); reconnect socket with this room and retry */
  onReconnectTo?: (roomName: string) => void;
}

/**
 * Custom hook to handle all WebSocket event listeners
 * Centralizes all socket event handling logic
 */
export function useSocketEvents({
  socket,
  roomState,
  setRoomState,
  setConnected,
  setMyPlayerId,
  setIsHost,
  setShowJoinRoom,
  showJoinRoom,
  setReconnecting,
  setReconnectAttempts,
  roomStateRef,
  isLeavingRef,
  reconnectTimeoutRef,
  pendingStartGameRef,
  isHost,
  showToast,
  startReconnection,
  clearReconnection,
  connectionErrorAttemptsRef,
  setConnectionError,
  failedRoomsRef,
  setRestoreAttemptSettled,
  onReconnectTo,
}: UseSocketEventsOptions) {
  // Track if we've already shown the "waiting for reconnection" toast
  // This prevents showing both "waiting" and "returning to home" toasts
  const hostDisconnectedToastShownRef = useRef(false);
  // Track previous hostDisconnectedAt state to detect host reconnection
  const previousHostDisconnectedAtRef = useRef<number | null | undefined>(undefined);
  // Show "Room not found" toast only once per join flow; cap reconnection after 3 attempts
  const roomNotFoundToastShownRef = useRef(false);
  const roomNotFoundAttemptsRef = useRef(0);
  const ROOM_NOT_FOUND_MAX_ATTEMPTS = 3;
  const showJoinRoomRef = useRef(showJoinRoom);
  showJoinRoomRef.current = showJoinRoom;
  // Cap reconnection join/get-room-state emissions to 3 so we never spam the backend
  const reconnectEmitCountRef = useRef(0);
  // Never show the same room-error toast twice in quick succession (e.g. "Already in room" x3 on refresh)
  const lastRoomErrorToastRef = useRef<{ message: string; at: number }>({ message: '', at: 0 });
  const ROOM_ERROR_TOAST_DEBOUNCE_MS = 3000;
  // Dedupe "game type not available" toast (BE may be rate-limited but FE still only show once per 3s)
  const lastGameTypeNotAllowedToastRef = useRef(0);
  const GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS = 3000;
  // SFX only on host; ref so handlers always see current value
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost;

  useEffect(() => {
    if (!socket) return;

    // Helper to update room state safely
    const updateRoomState = (updater: (prev: RoomState | null) => RoomState | null) => {
      setRoomState(updater);
    };

    // Helper to update room state for specific room
    const updateRoomStateForRoom = (
      roomName: string,
      updater: (prev: RoomState) => RoomState
    ) => {
      setRoomState((prev) => {
        if (prev && prev.roomName === roomName) {
          return updater(prev);
        }
        return prev;
      });
    };

    // ========== CONNECTION EVENTS ==========

    const handleConnect = (data: any) => {
      console.log('Connected to backend');
      setConnected(true);
      setMyPlayerId(socket.id || data?.id || null);

      // Clear reconnection state and connection error on successful connection
      clearReconnection();
      connectionErrorAttemptsRef.current = 0;
      setConnectionError(null);

      // Reconnection logic: prioritize typed room code (from URL) over sessionStorage
      // Check URL first, then fall back to sessionStorage if not expired
      const roomCodeFromUrl = getRoomCodeFromUrl();
      const hostCodeFromUrl = getHostCodeFromUrl();
      const savedData = RoomSessionStorage.getSavedRoomData();
      
      // IMPORTANT: If we have a host parameter in URL, we're trying to create a new admin room
      // Don't try to reconnect to old rooms in this case - let the room creation logic handle it
      if (hostCodeFromUrl) {
        console.log(`Host parameter in URL (${hostCodeFromUrl}) - skipping reconnection to allow room creation`);
        // Clear any old room data that doesn't match the host code
        if (savedData.roomName && savedData.roomName !== hostCodeFromUrl) {
          console.log(`Clearing old room data (${savedData.roomName}) - creating new admin room (${hostCodeFromUrl})`);
          RoomSessionStorage.clearRoomData();
          RoomSessionStorage.clearRoomExpiresAt();
        }
        return; // Don't attempt reconnection - room creation will happen via URL param handler
      }
      
      // Check if saved room is expired
      const isSavedRoomExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
      
      // Clear expired room data immediately if expired
      if (isSavedRoomExpired && savedData.roomName) {
        console.log(`Clearing expired room data on connect: ${savedData.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}`);
        RoomSessionStorage.clearRoomData();
        RoomSessionStorage.clearRoomExpiresAt();
        // Don't attempt reconnection if room is expired
        return;
      }
      
      // When user is on the join screen without a restorable session, JoinRoom handles join on click.
      // If they have saved player/host data (e.g. refresh on /join), still reconnect from here.
      const hasRestorableSession =
        !!savedData.roomName &&
        !isSavedRoomExpired &&
        (!!savedData.hostToken || !!savedData.playerName);

      if (showJoinRoomRef.current && !hasRestorableSession) {
        console.log('On join screen without saved session - JoinRoom handles join');
        return;
      }

      // Priority: 1. URL room code (user typed it), 2. sessionStorage (if not expired)
      const roomRaw = roomCodeFromUrl || (!isSavedRoomExpired && savedData.roomName ? savedData.roomName : null);
      const roomToReconnect = roomRaw ? formatRoomCode(roomRaw) : null;

      // Skip reconnection for rooms that recently failed (e.g. "Room not found") - use normalized key so URL "asda" matches stored "ASDA"
      if (roomToReconnect && failedRoomsRef?.current?.has(roomToReconnect)) {
        console.log('Skipping reconnection to recently failed room:', roomToReconnect);
        return;
      }
      // After 3 "room not found" attempts, stop reconnecting and don't emit join/get-room-state again
      if (roomNotFoundAttemptsRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
        console.log('Room not found max attempts reached, skipping reconnection');
        return;
      }
      
      // Max 3 reconnection join/get-room-state attempts - no unlimited retries
      if (reconnectEmitCountRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
        console.log('Reconnection emit cap reached (3), skipping');
        return;
      }

      // Reconnect if we have a room to rejoin: either player (playerName) or host (hostToken).
      // Use hostToken as the source of truth for host reconnection, not sessionStorage isHost -
      // sessionStorage is shared across tabs, so the player tab can overwrite isHost to false.
      if (roomToReconnect && (savedData.playerName || savedData.hostToken)) {
        // Clear saved room data if using URL code that doesn't match saved
        if (roomCodeFromUrl && savedData.roomName && formatRoomCode(roomCodeFromUrl) !== (savedData.roomName ? formatRoomCode(savedData.roomName) : '')) {
          console.log(`Clearing saved room data: URL code (${roomCodeFromUrl}) doesn't match saved (${savedData.roomName})`);
          RoomSessionStorage.clearRoomData();
          RoomSessionStorage.clearRoomExpiresAt();
        }
        
        setTimeout(() => {
          if (reconnectEmitCountRef.current >= ROOM_NOT_FOUND_MAX_ATTEMPTS) return;
          const currentRoomState = roomStateRef.current;
          if (!currentRoomState || currentRoomState.roomName !== roomToReconnect) {
            reconnectEmitCountRef.current += 1;
            const isHostReconnect = savedData.roomName === roomToReconnect && !!savedData.hostToken;
            console.log('Reconnecting to room:', roomToReconnect, 'asHost:', isHostReconnect, 'fromURL:', !!roomCodeFromUrl, `(emit ${reconnectEmitCountRef.current}/${ROOM_NOT_FOUND_MAX_ATTEMPTS})`);
            if (isHostReconnect) {
              socket.emit('get-room-state', {
                roomName: roomToReconnect,
                hostToken: savedData.hostToken || undefined,
              });
              // isHost will be set from room-state when it arrives (socket.id === state.hostId)
            } else {
              socket.emit('join-room', {
                roomName: roomToReconnect,
                playerName: savedData.playerName,
                avatar: savedData.avatar || undefined,
              });
            }
          } else {
            console.log('Already in room with state, skipping duplicate state request');
          }
        }, 200);
      }
    };

    const handleConnectError = (error: Error) => {
      console.log('Connection error:', error.message);
      if (isLeavingRef.current) {
        console.log('Connection error but user intentionally left - keeping connected=true');
        setConnected(true);
        return;
      }
      setConnected(false);
      
      // Track connection error attempts to detect when server is unreachable
      connectionErrorAttemptsRef.current += 1;
      const maxAttempts = 3; // Show error after 3 failed attempts
      
      // Check if error indicates server is unreachable
      const isUnreachable = error.message.includes('ERR_CONNECTION_REFUSED') || 
                           error.message.includes('xhr poll error') ||
                           error.message.includes('ECONNREFUSED') ||
                           error.message.includes('NetworkError');
      
      if (isUnreachable && connectionErrorAttemptsRef.current >= maxAttempts) {
        setConnectionError('Server is unreachable. Please check if the backend server is running.');
      } else if (connectionErrorAttemptsRef.current >= maxAttempts * 2) {
        // After even more attempts, show generic error
        setConnectionError('Unable to connect to the server after multiple attempts. The server may be down.');
      }
    };

    const handleDisconnect = () => {
      console.log('Disconnected from backend');

      if (isLeavingRef.current) {
        console.log('Disconnected but user intentionally left - keeping connected=true and skipping reconnection');
        setConnected(true);
        return;
      }

      // If there's no room in session storage, room was likely destroyed
      // Don't try to reconnect, just show start screen
      const savedRoomName = RoomSessionStorage.getRoomName();
      if (!savedRoomName || !roomStateRef.current) {
        console.log('Disconnected but no room in session storage - room was likely destroyed, showing start screen');
        setConnected(true);
        setRoomState(null);
        setShowJoinRoom(false); // Show RoomSetup (start screen)
        return;
      }

      setConnected(false);
      startReconnection();
    };

    // ========== ROOM EVENTS ==========

    const handleRoomCreated = (data: { roomCode: string; gameType: string; hostToken?: string }) => {
      console.log('Room created:', data);
      // Only creator receives this event (host device); play SFX
      playRoomCreated();
      roomNotFoundToastShownRef.current = false;
      roomNotFoundAttemptsRef.current = 0;
      reconnectEmitCountRef.current = 0;
      if (data.roomCode) {
        failedRoomsRef?.current?.delete(data.roomCode);
        RoomSessionStorage.setRoomName(data.roomCode);
        RoomSessionStorage.setIsHost(true);
        if (data.hostToken) {
          RoomSessionStorage.setHostToken(data.hostToken);
        }

        // Clear host parameter from URL so refresh shows root (avoids stale ?host= when room is gone)
        if (getHostCodeFromUrl() === data.roomCode) {
          clearQueryParamFromUrl('host');
        }

        // Clear /host entry path so refresh does not auto-create another room
        clearEntryRouteFromUrl();

        socket.emit('get-room-state', { roomName: data.roomCode });
        setIsHost(true);

        if (pendingStartGameRef.current) {
          setTimeout(() => {
            const hostToken = data.hostToken ?? RoomSessionStorage.getSavedRoomData()?.hostToken ?? undefined;
            socket.emit('start-game', { roomName: data.roomCode, hostToken });
            pendingStartGameRef.current = null;
          }, 200);
        }
      }
    };

    const handleRoomJoined = (data: { roomName: string; playerName: string; avatar?: string; playerId?: string }) => {
      console.log('Room joined:', data);
      if (isHostRef.current) playRoomJoined();
      roomNotFoundToastShownRef.current = false;
      roomNotFoundAttemptsRef.current = 0;
      reconnectEmitCountRef.current = 0;
      if (data.roomName) {
        failedRoomsRef?.current?.delete(data.roomName);
        RoomSessionStorage.setRoomName(data.roomName);
        RoomSessionStorage.setPlayerName(data.playerName);
        RoomSessionStorage.setIsHost(false);
        if (data.avatar) {
          RoomSessionStorage.setAvatar(data.avatar);
        }

        // Prefer stable backend player ID. Fall back to socket ID for backward compatibility.
        const resolvedPlayerId = data.playerId || socket.id || null;
        setMyPlayerId(resolvedPlayerId);
        if (resolvedPlayerId) {
          console.log(`Player rejoined - updated myPlayerId to: ${resolvedPlayerId}`);
        }

        setShowJoinRoom(false);
        setIsHost(false);

        clearEntryRouteFromUrl();

        setTimeout(() => {
          socket.emit('get-room-state', { roomName: data.roomName });
        }, 100);

        // Notify backend that we (a player) joined/rejoined so host gets updated player list
        // Critical for reconnection: when player reconnects after phone lock, host may not have
        // received the room-state broadcast; player-back-online triggers a second broadcast
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && !savedData.isHost) {
          setTimeout(() => {
            socket.emit('player-back-online', { roomName: data.roomName });
          }, 150);
        }
      }
    };

    const handleGameTypeChanged = (data: { roomName: string; gameType: string }) => {
      console.log('Game type changed:', data);
      if (data.roomName) {
        socket.emit('get-room-state', { roomName: data.roomName });
        // Clear last attempted game type since backend accepted it
        // We need to access the ref from App.tsx - this will be handled by room-state update
      }
    };

    const handleVotesUpdated = (data: { roomName: string; votes: Record<string, number> }) => {
      console.log('Votes updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        votes: data.votes,
      }));
    };

    const handleCategoryVotesUpdated = (data: {
      roomName: string;
      categoryVotes: Record<string, number>;
    }) => {
      console.log('Category votes updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        categoryVotes: data.categoryVotes,
      }));
    };

    const handlePlayerJoined = (data: any) => {
      console.log('Player joined:', data);
      if (isHostRef.current) playPlayerJoined();
      if (data.roomName && data.players) {
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          players: data.players,
        }));
        socket.emit('get-room-state', { roomName: data.roomName });
      }
    };

    const handlePlayerLeft = (data: any) => {
      console.log('Player left:', data);
      if (data.roomName && data.players) {
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          players: data.players,
        }));
        socket.emit('get-room-state', { roomName: data.roomName });
      }
    };

    const handleRoomState = (state: RoomState) => {
      const savedRoomName = RoomSessionStorage.getRoomName();
      const savedHostToken = RoomSessionStorage.getHostToken();
      const isReturnToWaiting =
        state.gameState === 'waiting' &&
        (savedRoomName === state.roomName || roomStateRef.current?.roomName === state.roomName);
      const isHostSessionRestore =
        !!savedHostToken &&
        savedRoomName === state.roomName &&
        !roomStateRef.current;

      if (isLeavingRef.current && !isReturnToWaiting && !isHostSessionRestore) {
        console.log('Ignoring room-state event - user is leaving');
        return;
      }
      if (isLeavingRef.current && (isReturnToWaiting || isHostSessionRestore)) {
        isLeavingRef.current = false;
      }

      // Check if the room we're trying to reconnect to is expired
      const savedData = RoomSessionStorage.getSavedRoomData();
      const isExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
      if (isExpired && savedData.roomName === state.roomName) {
        console.log(`Ignoring room-state for expired room: ${state.roomName} expired at ${new Date(savedData.expiresAt!).toISOString()}`);
        RoomSessionStorage.clearAll();
        setRoomState(null);
        setShowJoinRoom(false); // Show room setup page
        setRestoreAttemptSettled?.(true);
        return;
      }

      const savedIsHost = RoomSessionStorage.getIsHost();
      
      // If we don't have a saved room name, but we're receiving room-state, it might be:
      // 1. Premium room creation via URL (room-state arrives before room-created)
      // 2. Reconnection scenario where session storage was cleared
      // In these cases, if socket.id matches hostId AND we're not currently in join flow, we should accept it
      if (!savedRoomName) {
        // Check if we're the host (socket.id matches hostId) - this indicates we just created the room
        const isHostBySocketId = socket.id && state.hostId && socket.id === state.hostId;
        // CRITICAL: Only set as host if we're NOT in the join room flow (showJoinRoom is false)
        // If showJoinRoom is true, we're actively joining as a player, so don't override that
        const isInJoinFlow = showJoinRoom;
        if (isHostBySocketId && !isInJoinFlow) {
          // We're the host but don't have room saved - this is likely a premium room creation
          // Save the room name and host status now
          console.log(`Accepting room-state for new room ${state.roomName} (host detected, not in join flow)`);
          RoomSessionStorage.setRoomName(state.roomName);
          RoomSessionStorage.setIsHost(true);
        } else {
          // Not the host and no saved room - ignore it (might be a stale event)
          // OR we're in join flow, so don't set as host even if socket.id matches
          if (isHostBySocketId && isInJoinFlow) {
            console.log('Ignoring host detection - currently in join room flow, joining as player');
          } else {
            console.log('Ignoring room-state event - no saved room in session and not the host');
          }
          return;
        }
      } else if (savedRoomName !== state.roomName) {
        // We have a saved room but it doesn't match - ignore it
        console.log(
          `Ignoring room-state event - wrong room (saved: ${savedRoomName}, received: ${state.roomName})`
        );
        return;
      }

      const currentRoomState = roomStateRef.current;
      if (!currentRoomState || currentRoomState.roomName === state.roomName) {
        // Keep a stable identity across reconnects by deriving the local player id from room-state.
        // Only set when exactly one player matches by name to avoid wrong identity with duplicate names.
        const localPlayerName = RoomSessionStorage.getPlayerName();
        if (localPlayerName && Array.isArray(state.players)) {
          const matches = state.players.filter((p: any) => p?.name === localPlayerName);
          if (matches.length === 1 && matches[0]?.id) {
            setMyPlayerId(matches[0].id);
          }
        }

        console.log(`📊 Room-state received for room ${state.roomName}: ${state.players?.length || 0} players`);
        if (state.players && state.players.length > 5) {
          console.warn(`⚠️ Room-state has more than 5 players (${state.players.length}). All should be displayed.`);
        }
        
        // Check for host reconnection (host was disconnected but is now back)
        // hostDisconnectedAt is null/undefined when host is connected
        const currentHostDisconnectedAt = (state as any).hostDisconnectedAt;
        const previousHostDisconnectedAt = previousHostDisconnectedAtRef.current;
        
        // Normalize current value (undefined -> null for consistency)
        const normalizedCurrent = currentHostDisconnectedAt === undefined ? null : currentHostDisconnectedAt;
        
        // If host is disconnected (currentHostDisconnectedAt is a number/timestamp),
        // update our tracking ref so we can detect reconnection later
        if (!isHost && typeof normalizedCurrent === 'number') {
          // Host is currently disconnected - update ref if not already set
          if (previousHostDisconnectedAt === null || previousHostDisconnectedAt === undefined) {
            previousHostDisconnectedAtRef.current = normalizedCurrent;
            console.log('🔍 Detected host disconnection from room-state, set previousHostDisconnectedAtRef to:', normalizedCurrent);
          }
        }
        
        // Debug logging for host reconnection detection
        if (!isHost && previousHostDisconnectedAt !== undefined && previousHostDisconnectedAt !== null) {
          console.log('🔍 Host reconnection check:', {
            previous: previousHostDisconnectedAt,
            current: normalizedCurrent,
            previousType: typeof previousHostDisconnectedAt,
            currentType: typeof normalizedCurrent,
            wasDisconnected: typeof previousHostDisconnectedAt === 'number',
            isNowConnected: normalizedCurrent === null
          });
        }
        
        // Host reconnected if:
        // 1. Previous state had hostDisconnectedAt set (host was disconnected) - must be a number (timestamp)
        // 2. Current state has hostDisconnectedAt as null (host is now connected)
        // 3. We're not the host (only show to players)
        // 4. HostId matches (host is actually in the same room)
        // IMPORTANT: Only show "host reconnected" if host is actually in THIS room
        // If host moved to a different room, the host-moved-to-new-room event will handle it
        if (
          !isHost && 
          previousHostDisconnectedAt !== undefined &&
          previousHostDisconnectedAt !== null &&
          typeof previousHostDisconnectedAt === 'number' &&
          normalizedCurrent === null &&
          state.hostId && // Host ID exists
          state.roomName === savedRoomName // Host is in the same room we're in
        ) {
          showToast(t('toasts.hostReconnected'), 'success', 5000);
          console.log('✅ Host reconnected - showing toast to players', {
            previous: previousHostDisconnectedAt,
            current: normalizedCurrent,
            hostId: state.hostId,
            roomName: state.roomName
          });
        }
        
        // Update previous state to track changes
        if (previousHostDisconnectedAt !== normalizedCurrent) {
          previousHostDisconnectedAtRef.current = normalizedCurrent;
        }

        if (currentRoomState && currentRoomState.players && state.players) {
          state.players.forEach((player: any) => {
            const prevPlayer = currentRoomState.players.find((p: any) => p.id === player.id);
            if (prevPlayer && player.score !== prevPlayer.score) {
              console.log(
                `📊 Score update received: ${player.name} - Old: ${prevPlayer.score}, New: ${player.score}, Diff: ${player.score - prevPlayer.score}`
              );
            }
          });
        }

        // When game is finished, preserve finalLeaderboard/gameStatistics from previous state
        // if server didn't send them (server room-state doesn't include these; we got them from game-ended).
        const shouldHoldBonusScreen =
          !!currentRoomState?.isBonusLevel &&
          currentRoomState.gameState === 'playing' &&
          state.gameState === 'playing' &&
          !state.isBonusLevel &&
          !state.currentQuestion;

        const mergedState: RoomState = shouldHoldBonusScreen
          ? {
              ...state,
              // Hold the bonus UI until the next real question arrives so the host
              // doesn't flash the generic loading fallback between states.
              isBonusLevel: true,
              bonusLevelPayload: currentRoomState?.bonusLevelPayload,
              bonusStartTime: currentRoomState?.bonusStartTime,
            }
          : { ...state };
        if (state.gameState === 'finished' && currentRoomState) {
          if (currentRoomState.finalLeaderboard != null) mergedState.finalLeaderboard = currentRoomState.finalLeaderboard;
          if (currentRoomState.gameStatistics != null) mergedState.gameStatistics = currentRoomState.gameStatistics;
        }
        // Puzzle bonus: server doesn't store imageUrl; preserve it when room-state overwrites so host/players keep the image.
        if (
          state.isBonusLevel &&
          state.bonusLevelPayload &&
          currentRoomState?.bonusLevelPayload?.imageUrl &&
          state.bonusLevelPayload.questionId === currentRoomState.bonusLevelPayload.questionId &&
          !state.bonusLevelPayload.imageUrl
        ) {
          mergedState.bonusLevelPayload = { ...state.bonusLevelPayload, imageUrl: currentRoomState.bonusLevelPayload.imageUrl };
        }

        setRoomState(mergedState);
        setRestoreAttemptSettled?.(true);

        // Store expiration date if this is a premium room
        if (state.isPremium && (state as any).expiresAt) {
          RoomSessionStorage.setRoomExpiresAt((state as any).expiresAt);
          console.log(`💾 Stored room expiration: ${new Date((state as any).expiresAt).toISOString()}`);
        } else if (!state.isPremium) {
          // Clear expiration for non-premium rooms
          RoomSessionStorage.clearRoomExpiresAt();
        }

        // Server is the single source of truth for this tab's host role (UI only).
        // Derive isHost from socket.id === state.hostId so this tab shows correct role.
        // Do NOT write to RoomSessionStorage here: sessionStorage is shared across tabs. If a
        // player tab receives room-state and we set isHost false / clearHostData(), we would
        // wipe the real host's token in another tab and break host reconnection/refresh.
        const isCurrentHost = socket.id && state.hostId && socket.id === state.hostId;
        setIsHost(!!isCurrentHost);
        if (savedIsHost && !isCurrentHost && socket.id && state.hostId) {
          console.log('Host status corrected from room-state: we are not the host (socket.id !== hostId)');
        }
      }
    };

    const handleRoomError = (error: { message: string; isPremiumRoom?: boolean; roomName?: string; reconnectTo?: string }) => {
      console.error('Room error:', error);

      // Production (Cloudflare): backend says we're on wrong Durable Object - reconnect to correct DO and retry
      if (error.reconnectTo && onReconnectTo) {
        console.log('Reconnecting to correct DO for room:', error.reconnectTo);
        onReconnectTo(error.reconnectTo);
        return;
      }

      // "Already in room" on reconnection (e.g. refresh while waiting for host): don't show toast; we're already in the right place.
      if (error.message && error.message.includes('Already in room')) {
        const savedData = RoomSessionStorage.getSavedRoomData();
        if (savedData.roomName && !roomStateRef.current?.roomName) {
          socket.emit('get-room-state', { roomName: savedData.roomName });
        }
        return;
      }

      // Check if this is an admin/premium room already in use or already hosted error
      const isAdminRoomInUse = error.message && (
        error.message.includes('already in use') ||
        error.message.includes('Room code already in use') ||
        error.message.includes('Room already hosted by someone else')
      );
      
      // Check if this is an admin room code (TOYO, GAME, ROOM)
      const hostCodeFromUrl = getHostCodeFromUrl();
      const isAdminRoom = hostCodeFromUrl && ['TOYO', 'GAME', 'ROOM'].includes(hostCodeFromUrl.toUpperCase());
      
      // Only redirect to home for specific errors that indicate the room was destroyed or doesn't exist
      // Don't redirect for validation errors (like "room code not available", "not available", etc.) - keep user on join screen
      const isValidationError = error.message && (
        error.message.includes('not available') ||
        error.message.includes('is not available') ||
        error.message.includes('Invalid room code') ||
        error.message.includes('Room code') && error.message.includes('not available') ||
        isAdminRoomInUse // Don't redirect for admin room in use - let user try another
      );

      const isRoomNotFound = error.message && (
        error.message.includes('Room not found') ||
        error.message.includes('room not found') ||
        error.message.includes('Room destroyed') ||
        error.message.includes('room was closed')
      ) && !isValidationError;

      if (isRoomNotFound) {
        // User intentionally went back/home - don't show toast or re-run cleanup (already done in handleGoHome)
        if (isLeavingRef.current) {
          return;
        }
        // Add to failed rooms (normalized) so any reconnection/connect handler skips this room immediately
        const failedRoomRaw = error.roomName || RoomSessionStorage.getSavedRoomData().roomName;
        const failedRoom = failedRoomRaw ? formatRoomCode(failedRoomRaw) : null;
        if (failedRoom && failedRoomsRef?.current) {
          failedRoomsRef.current.add(failedRoom);
        }
        clearReconnection();
        setReconnecting(false);
        setReconnectAttempts(0);
        roomNotFoundAttemptsRef.current += 1;
        reconnectEmitCountRef.current = ROOM_NOT_FOUND_MAX_ATTEMPTS; // cap so no more reconnection emits
        const attempts = roomNotFoundAttemptsRef.current;
        if (attempts >= ROOM_NOT_FOUND_MAX_ATTEMPTS) {
          console.log('Room not found: max attempts reached, redirecting to game setup (no more reconnection)');
        } else {
          console.log(`Room not found (attempt ${attempts}/${ROOM_NOT_FOUND_MAX_ATTEMPTS}), redirecting to game setup`);
        }
        // Clear room (and host data) so we don't retry this room; keep player name and avatar for next join
        RoomSessionStorage.clearRoomKeepPlayer();
        setRoomState(null);
        setIsHost(false);
        const wasOnJoinScreen = showJoinRoomRef.current || isJoinEntryPath();
        if (wasOnJoinScreen) {
          // Stay on join form so code, name, and avatar are preserved
          setShowJoinRoom(true);
          navigateToJoin({ replace: true });
        } else {
          setShowJoinRoom(false);
          clearQueryParamFromUrl('room');
          clearEntryRouteFromUrl();
        }
        setRestoreAttemptSettled?.(true);
        if (wasOnJoinScreen) {
          dispatchJoinRoomError(JOIN_ROOM_NOT_FOUND_MESSAGE());
        } else if (!roomNotFoundToastShownRef.current) {
          roomNotFoundToastShownRef.current = true;
          showToast(t('toasts.roomNotExist'), 'warning');
        }
        return;
      }

      // Show appropriate error message for non-"room not found" errors (never show same toast 3 times)
      const isDuplicateName = error.message && (
        error.message.includes('name') &&
        (error.message.includes('already') || error.message.includes('choose another name'))
      );
      const isHostAlreadyUsing = error.message && (
        error.message.includes('already in use') ||
        error.message.includes('already hosted') ||
        error.message.includes('already in use by another host')
      );
      const messageToShow = isDuplicateName
        ? t('toasts.duplicatePlayerName')
        : isHostAlreadyUsing
          ? t('toasts.hostAlreadyUsingRoom')
          : isAdminRoomInUse && isAdminRoom
            ? t('toasts.adminRoomInUse')
            : translateBackendError(error.message);
      const now = Date.now();
      const last = lastRoomErrorToastRef.current;
      if (last.message === messageToShow && now - last.at < ROOM_ERROR_TOAST_DEBOUNCE_MS) return;
      lastRoomErrorToastRef.current = { message: messageToShow, at: now };

      showToast(messageToShow, 'error');

      // /host auto-create failed — return to root hub instead of infinite loader
      if (isHostEntryPath() && !hostCodeFromUrl && !getRoomCodeFromUrl()) {
        clearEntryRouteFromUrl();
        navigateToRoot(true);
        setShowJoinRoom(false);
        setRestoreAttemptSettled?.(true);
      }

      // If we had ?host= and create-room failed (e.g. host already using), clear URL and mark failed so we don't retry
      if (hostCodeFromUrl && failedRoomsRef?.current) {
        failedRoomsRef.current.add(hostCodeFromUrl);
        clearQueryParamFromUrl('host');
      }
    };

    const handlePremiumRoomNotActivated = (data: { roomName: string; canCreate: boolean }) => {
      console.log('Premium room not activated, can create:', data);
      
      if (data.canCreate && data.roomName) {
        // Room is available and can be created - automatically navigate to host creation URL
        console.log(`Premium room ${data.roomName} is available - redirecting to create as host`);
        window.location.href = `/?host=${data.roomName}`;
      }
    };

    const handleRoomDestroyed = (data: { roomName: string; reason: string }) => {
      console.log('Room destroyed:', data);

      const userInitiatedLeave = isLeavingRef.current;
      
      // Clear reconnection state immediately - room is gone, no point reconnecting
      clearReconnection();
      setReconnecting(false);
      setReconnectAttempts(0);
      
      // For premium rooms (with expiration), preserve hostToken when room is destroyed so host can rejoin.
      // Do NOT preserve for admin rooms (TOYO, GAME, ROOM) - they have no expiresAt, and preserving
      // causes get-room-state spam and stale reconnection attempts until host creates a new room.
      const savedData = RoomSessionStorage.getSavedRoomData();
      const hasPremiumExpiration = savedData.expiresAt != null && Date.now() < savedData.expiresAt;
      const isPremiumRoom = hasPremiumExpiration && savedData.isHost && savedData.hostToken && savedData.roomName === data.roomName;
      const savedHostToken = isPremiumRoom ? savedData.hostToken : null;
      const savedRoomName = isPremiumRoom ? savedData.roomName : null;

      // room-not-found: clear room/host only, keep player name and avatar for next join
      if (data.reason === 'room-not-found') {
        RoomSessionStorage.clearRoomKeepPlayer();
      } else {
        RoomSessionStorage.clearAll();
        // Restore hostToken and roomName only for premium (expiring) rooms to allow reconnection
        if (isPremiumRoom && savedHostToken && savedRoomName) {
          console.log(`Preserving hostToken for premium room ${savedRoomName} after room destruction to allow reconnection`);
          RoomSessionStorage.setRoomName(savedRoomName);
          RoomSessionStorage.setHostToken(savedHostToken);
          RoomSessionStorage.setIsHost(true);
        }
      }
      
      setRoomState(null);
      setIsHost(false);
      pendingStartGameRef.current = null;
      
      // Immediately settle the restore attempt so the loading shell disappears and setup tiles show
      setRestoreAttemptSettled?.(true);
      
      // Reset connection state - we're back to start screen, mark as connected
      // This prevents showing "connecting to server" when socket is disconnected
      setConnected(true);

      // Only go to start screen when room is destroyed if user didn't initiate leave
      const wasOnJoinScreen = showJoinRoomRef.current || isJoinEntryPath();
      if (!userInitiatedLeave && !wasOnJoinScreen) {
        setShowJoinRoom(false);
      } else if (!userInitiatedLeave && wasOnJoinScreen) {
        setShowJoinRoom(true);
        navigateToJoin({ replace: true });
      }

      // Reset the host disconnected toast flag when room is destroyed
      hostDisconnectedToastShownRef.current = false;
      
      if (data.reason === 'host-left') {
        // Only show toast to players, not to the host who intentionally left
        // If we already showed "waiting for reconnection", don't show this one
        // (the waiting toast is more informative)
        // Also don't show if user initiated the leave (they're navigating intentionally)
        if (!isHost && !hostDisconnectedToastShownRef.current && !userInitiatedLeave) {
          showToast(t('toasts.hostLeftRoom'), 'warning');
        }
      } else if (data.reason === 'no-players') {
        console.log('Room closed: no players remaining');
      } else if (data.reason === 'room-not-found') {
        const failedRoom = data.roomName ? formatRoomCode(data.roomName) : null;
        if (failedRoom && failedRoomsRef?.current) {
          failedRoomsRef.current.add(failedRoom);
        }
        roomNotFoundAttemptsRef.current += 1;
        if (!userInitiatedLeave) {
          if (wasOnJoinScreen) {
            dispatchJoinRoomError(JOIN_ROOM_NOT_FOUND_MESSAGE());
          } else if (!roomNotFoundToastShownRef.current) {
            roomNotFoundToastShownRef.current = true;
            showToast(t('toasts.roomNotExist'), 'warning');
          }
        }
        console.log('Room not found - room may have been closed');
      } else if (data.reason === 'admin-kill') {
        showToast(t('toasts.roomClosedByAdmin'), 'warning');
        console.log('Room closed by admin');
      } else if (data.reason === 'lobby-idle-timeout') {
        if (!userInitiatedLeave) {
          showToast(t('toasts.lobbyIdleClosed'), 'warning');
        }
        console.log('Room closed due to lobby idle timeout');
      }

      isLeavingRef.current = false;
    };

    const handleHostDisconnected = (data: { roomName: string; message: string }) => {
      console.log('Host disconnected:', data);
      const current = roomStateRef.current;
      if (!isHost && current && current.roomName === data.roomName) {
        // Only show toast if we haven't shown it yet
        if (!hostDisconnectedToastShownRef.current) {
          showToast(t('toasts.hostLeftWaiting'), 'warning');
          hostDisconnectedToastShownRef.current = true;
        }
        // Mark that host is disconnected by setting previousHostDisconnectedAtRef to a timestamp
        // This ensures we can detect when host reconnects (when it becomes null in room-state)
        // Use a timestamp to indicate host was disconnected
        if (previousHostDisconnectedAtRef.current === null || previousHostDisconnectedAtRef.current === undefined) {
          previousHostDisconnectedAtRef.current = Date.now();
          console.log('🔍 Marked host as disconnected in previousHostDisconnectedAtRef:', previousHostDisconnectedAtRef.current);
        }
      }
    };

    const handleHostMovedToNewRoom = (data: { oldRoomName: string; newRoomName: string; message: string }) => {
      console.log('Host moved to new room:', data);
      const current = roomStateRef.current;
      if (!isHost && current && current.roomName === data.oldRoomName) {
        // Host moved to a new room - redirect player to the new room
        console.log(`Redirecting player from room ${data.oldRoomName} to new room ${data.newRoomName}`);
        showToast(t('toasts.hostMovedToNewRoom', { roomName: data.newRoomName }), 'info', 3000);
        
        // Update session storage with new room name
        const savedPlayerName = RoomSessionStorage.getPlayerName();
        const savedAvatar = RoomSessionStorage.getAvatar();
        
        if (savedPlayerName && socket) {
          // Clear old room data
          RoomSessionStorage.clearAll();
          
          // Set new room data
          RoomSessionStorage.setRoomName(data.newRoomName);
          RoomSessionStorage.setPlayerName(savedPlayerName);
          if (savedAvatar) {
            RoomSessionStorage.setAvatar(savedAvatar);
          }
          RoomSessionStorage.setIsHost(false);
          
          // Join the new room
          setTimeout(() => {
            socket.emit('join-room', {
              roomName: data.newRoomName,
              playerName: savedPlayerName,
              avatar: savedAvatar || undefined,
            });
          }, 500); // Small delay to ensure state is cleared
        }
      }
    };

    const handleLobbyIdleWarning = (data: {
      roomName: string;
      remainingSeconds: number;
      idleTimeoutMs: number;
    }) => {
      const current = roomStateRef.current;
      if (
        isHost &&
        current &&
        current.roomName === data.roomName &&
        current.gameState === 'waiting'
      ) {
        const seconds = Math.max(0, Math.ceil(Number(data.remainingSeconds) || 0));
        showToast(t('toasts.lobbyIdleWarning', { seconds }), 'warning', 7000);
      }
    };

    // ========== GAME EVENTS ==========

    const handleGameStarted = (data: any) => {
      console.log('Game started:', data);
      if (isHostRef.current) playGameStart();
      pendingStartGameRef.current = null;
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        gameState: 'playing',
        currentQuestion: null,
      }));
    };

    // Update room state from bonus-level-start so we show bonus screen even if room-state broadcast was missed
    // (same pattern as re-sending question to reconnected host for normal questions)
    const handleBonusLevelStart = (data: any) => {
      if (isLeavingRef.current) return;
      const savedRoomName = RoomSessionStorage.getRoomName();
      if (!savedRoomName) return;
      setRoomState((prev) => {
        if (!prev || prev.roomName !== savedRoomName) return prev;
        console.log('Bonus level start received, updating room state');
        return {
          ...prev,
          isBonusLevel: true,
          bonusLevelPayload: data,
          bonusStartTime: data.startTime ?? Date.now(),
        };
      });
    };

    const handleBonusPuzzleImage = (data: { imageUrl?: string }) => {
      if (isLeavingRef.current) return;
      const imageUrl = data?.imageUrl;
      if (typeof imageUrl !== 'string') return;
      setRoomState((prev) => {
        if (!prev?.isBonusLevel || !prev.bonusLevelPayload) return prev;
        return {
          ...prev,
          bonusLevelPayload: { ...prev.bonusLevelPayload, imageUrl },
        };
      });
    };

    const mergeBonusLeaderboard = (leaderboard: Array<{ playerId: string; playerName: string; score: number }>) => {
      if (!Array.isArray(leaderboard) || leaderboard.length === 0) return;
      setRoomState((prev) => {
        if (!prev?.isBonusLevel) return prev;
        const players = syncPlayersFromLeaderboard(prev.players ?? [], leaderboard);
        return { ...prev, players };
      });
    };

    // Merge leaderboard from bonus-level-result so roomState.players have correct scores
    const handleBonusLevelResult = (data: { leaderboard?: Array<{ playerId: string; playerName: string; score: number }> }) => {
      if (isLeavingRef.current) return;
      mergeBonusLeaderboard(data?.leaderboard ?? []);
    };

    // Merge leaderboard from bonus-points-updated (late submissions) for robust score display
    const handleBonusPointsUpdated = (data: { leaderboard?: Array<{ playerId: string; playerName: string; score: number }> }) => {
      if (isLeavingRef.current) return;
      mergeBonusLeaderboard(data?.leaderboard ?? []);
    };

    const handleQuestion = (data: any) => {
      console.log('Question received:', data);
      updateRoomState((prev) => {
        if (prev) {
          // CRITICAL: Do NOT emit get-room-state here. We already have room state and the question.
          // Emitting would trigger backend to send room-state + an extra 'question' event. That extra
          // question (from get-room-state handler) lacked clues for What Could It Be, overwriting our
          // good question and causing clues to flash/disappear. Also caused console spam in prod.
          return {
            ...prev,
            gameState: 'playing',
            currentQuestion: data,
            // Update questionStartTime from question event if provided (for reconnection scenarios)
            questionStartTime: data.startTime || prev.questionStartTime,
            // Clear bonus state: receiving a normal question means bonus is over
            isBonusLevel: false,
            bonusLevelPayload: undefined,
            bonusStartTime: undefined,
          };
        } else {
          const savedRoomName = RoomSessionStorage.getRoomName();
          if (savedRoomName) {
            console.log('Question received before room state, requesting room state for:', savedRoomName);
            socket.emit('get-room-state', { roomName: savedRoomName });
          }
        }
        return prev;
      });

      // Only sync to 'playing' when we receive a question if we're not already in 'finished'
      // (never overwrite finished with playing – e.g. stale question after game ended)
      if (roomStateRef.current?.gameState !== 'finished') {
        updateRoomState((prev) => {
          if (prev) {
            return { ...prev, gameState: 'playing' };
          }
          return prev;
        });
      }
    };

    const handleQuestionResult = (data: any) => {
      console.log('Question result:', data);
      updateRoomState((prev) => {
        if (!prev) return prev;
        // Merge leaderboard from result into room state so scores are correct before
        // bonus-level-start switches to BonusHostScreen (which uses roomState.players).
        // Otherwise we'd show a drop when bonus screen loads if room-state hasn't arrived yet.
        // Leaderboard is authoritative roster + scores (server excludes intentionally-left players).
        const leaderboard = data?.leaderboard as Array<{ playerId: string; playerName: string; score: number }> | undefined;
        const players = syncPlayersFromLeaderboard(prev.players ?? [], leaderboard);
        return {
          ...prev,
          currentQuestion: null,
          gameState: 'playing',
          players: players ?? prev.players,
        };
      });
    };

    const handleGameEnded = (data: any) => {
      console.log('Game ended:', data);
      if (isHostRef.current) playGameFinished();
      updateRoomState((prev) => {
        if (prev) {
          return {
            ...prev,
            gameState: 'finished',
            currentQuestion: null,
            isBonusLevel: false,
            finalLeaderboard: data.leaderboard ?? prev.players?.map((p: any) => ({ playerId: p.id, playerName: p.name, score: p.score })) ?? [],
            gameStatistics: data.statistics ?? undefined,
          };
        }
        return prev;
      });
    };

    const handleGamePaused = (data: { roomName: string }) => {
      console.log('Game paused:', data);
      updateRoomState((prev) => {
        if (!prev || prev.roomName !== data.roomName) return prev;
        return { ...prev, isPaused: true };
      });
    };

    const handleGameResumed = (data: { roomName: string }) => {
      console.log('Game resumed:', data);
      updateRoomState((prev) => {
        if (!prev || prev.roomName !== data.roomName) return prev;
        return { ...prev, isPaused: false };
      });
    };

    const handleReturnToWaiting = (data: { roomName: string }) => {
      console.log('Returning to waiting:', data);
      if (isHostRef.current) playReturnToWaiting();
      // Back to lobby is not leaving the room — clear stale flag (e.g. from beforeunload/tab switch)
      isLeavingRef.current = false;
      if (data.roomName) {
        // Don't use sessionStorage for isHost - it's shared across tabs. If we set isHost from
        // sessionStorage here, the player tab could show host UI (e.g. GameSelection) when it
        // receives return-to-waiting and sessionStorage was overwritten by the host tab.
        setShowJoinRoom(false);
        updateRoomStateForRoom(data.roomName, (prev) => ({
          ...prev,
          gameState: 'waiting',
          currentQuestion: null,
          questionStartTime: undefined,
          isBonusLevel: false,
          bonusLevelPayload: undefined,
          bonusStartTime: undefined,
          isPaused: false,
          finalLeaderboard: undefined,
          gameStatistics: undefined,
        }));
        const savedData = RoomSessionStorage.getSavedRoomData();
        setTimeout(() => {
          if (savedData.hostToken) {
            socket.emit('get-room-state', { roomName: data.roomName, hostToken: savedData.hostToken });
          } else {
            socket.emit('get-room-state', { roomName: data.roomName });
          }
        }, 100);
        // isHost will be set correctly when room-state arrives (socket.id === state.hostId)
      }
    };

    const handleGameError = (error: { message: string }) => {
      console.error('Game error:', error);
      if (error.message === 'Room not found' && pendingStartGameRef.current) {
        const savedRoom = RoomSessionStorage.getSavedRoomData().roomName;
        const roomCode = savedRoom ? formatRoomCode(savedRoom) : undefined;
        console.log('Room not found when starting game, creating new room with gameType:', pendingStartGameRef.current.gameType);
        if (roomCode && onReconnectTo) {
          onReconnectTo(roomCode);
        }
        socket.emit('create-room', {
          gameType: pendingStartGameRef.current.gameType,
          ...(roomCode ? { roomCode } : {}),
        });
        return;
      }
      // Don't show toast for premium errors - frontend handles redirect
      if (error.message && error.message.toLowerCase().includes('premium')) {
        console.log('Premium error from backend - frontend will handle redirect');
        pendingStartGameRef.current = null;
        return;
      }
      // Dedupe "game type not available" toast so we don't spam when BE was rate-limited
      if (error.message && error.message.includes('This game type is not available')) {
        const now = Date.now();
        if (now - lastGameTypeNotAllowedToastRef.current < GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS) {
          pendingStartGameRef.current = null;
          return;
        }
        lastGameTypeNotAllowedToastRef.current = now;
      }
      const gameErrorMessage = error.message
        ? (error.message.includes('This game type is not available')
            ? t('toasts.gameTypeNotAvailable')
            : translateBackendError(error.message))
        : t('toasts.gameError');
      showToast(gameErrorMessage, 'error');
      pendingStartGameRef.current = null;
    };

    const handleGameTypeError = (error: { message: string }) => {
      console.error('Game type error:', error);
      const msg = error.message
        ? (error.message.includes('This game type is not available')
            ? t('toasts.gameTypeNotAvailable')
            : translateBackendError(error.message))
        : t('toasts.gameTypeNotAvailable');
      const now = Date.now();
      if (now - lastGameTypeNotAllowedToastRef.current < GAME_TYPE_NOT_ALLOWED_TOAST_DEBOUNCE_MS) return;
      lastGameTypeNotAllowedToastRef.current = now;
      showToast(msg, 'error');
    };

    const handleRoomSettingsUpdated = (data: { roomName: string; settings: any }) => {
      console.log('Room settings updated:', data);
      updateRoomStateForRoom(data.roomName, (prev) => ({
        ...prev,
        settings: data.settings,
      }));
    };

    // ========== REGISTER ALL EVENT HANDLERS ==========

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleConnectError);

    // If socket is already connected, or connects shortly after (e.g. player reconnection
    // page load where connect can fire before handlers are registered), run reconnection
    // logic so we emit join-room / get-room-state and restore room state.
    const runReconnectionIfConnected = () => {
      if (socket.connected) {
        handleConnect({ id: socket.id ?? undefined });
      }
    };
    runReconnectionIfConnected();
    const t400 = setTimeout(runReconnectionIfConnected, 400);
    const t800 = setTimeout(runReconnectionIfConnected, 800);
    const t1200 = setTimeout(runReconnectionIfConnected, 1200);
    socket.on('disconnect', handleDisconnect);
    socket.on('room-created', handleRoomCreated);
    socket.on('room-joined', handleRoomJoined);
    socket.on('game-type-changed', handleGameTypeChanged);
    socket.on('votes-updated', handleVotesUpdated);
    socket.on('category-votes-updated', handleCategoryVotesUpdated);
    socket.on('player-joined', handlePlayerJoined);
    socket.on('player-left', handlePlayerLeft);
    socket.on('room-state', handleRoomState);
    socket.on('room-error', handleRoomError);
    socket.on('premium-room-not-activated', handlePremiumRoomNotActivated);
    socket.on('room-destroyed', handleRoomDestroyed);
    socket.on('host-disconnected', handleHostDisconnected);
    socket.on('host-moved-to-new-room', handleHostMovedToNewRoom);
    socket.on('lobby-idle-warning', handleLobbyIdleWarning);
    socket.on('game-started', handleGameStarted);
    socket.on('bonus-level-start', handleBonusLevelStart);
    socket.on('bonus-puzzle-image', handleBonusPuzzleImage);
    socket.on('bonus-level-result', handleBonusLevelResult);
    socket.on('bonus-points-updated', handleBonusPointsUpdated);
    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-paused', handleGamePaused);
    socket.on('game-resumed', handleGameResumed);
    socket.on('return-to-waiting', handleReturnToWaiting);
    socket.on('game-error', handleGameError);
    socket.on('game-type-error', handleGameTypeError);
    socket.on('room-settings-updated', handleRoomSettingsUpdated);

    // ========== CLEANUP ==========

    return () => {
      clearTimeout(t400);
      clearTimeout(t800);
      clearTimeout(t1200);
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleConnectError);
      socket.off('disconnect', handleDisconnect);
      socket.off('room-created', handleRoomCreated);
      socket.off('room-joined', handleRoomJoined);
      socket.off('game-type-changed', handleGameTypeChanged);
      socket.off('votes-updated', handleVotesUpdated);
      socket.off('category-votes-updated', handleCategoryVotesUpdated);
      socket.off('player-joined', handlePlayerJoined);
      socket.off('player-left', handlePlayerLeft);
      socket.off('room-state', handleRoomState);
      socket.off('room-error', handleRoomError);
      socket.off('premium-room-not-activated', handlePremiumRoomNotActivated);
      socket.off('room-destroyed', handleRoomDestroyed);
      socket.off('host-disconnected', handleHostDisconnected);
      socket.off('host-moved-to-new-room', handleHostMovedToNewRoom);
      socket.off('lobby-idle-warning', handleLobbyIdleWarning);
      socket.off('game-started', handleGameStarted);
      socket.off('bonus-level-start', handleBonusLevelStart);
      socket.off('bonus-puzzle-image', handleBonusPuzzleImage);
      socket.off('bonus-level-result', handleBonusLevelResult);
      socket.off('bonus-points-updated', handleBonusPointsUpdated);
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-paused', handleGamePaused);
      socket.off('game-resumed', handleGameResumed);
      socket.off('return-to-waiting', handleReturnToWaiting);
      socket.off('game-error', handleGameError);
      socket.off('game-type-error', handleGameTypeError);
      socket.off('room-settings-updated', handleRoomSettingsUpdated);
    };
    // Note: We intentionally don't include all setters/refs in deps to avoid re-registering
    // The handlers use the latest values via closures. Including them would cause
    // unnecessary re-registration of all event handlers on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    socket,
    isHost,
    showToast,
    startReconnection,
    clearReconnection,
    onReconnectTo,
  ]);
}
